import React, { useState, memo } from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import SortableTask from './SortableTask';

const DroppableColumn = memo(({ column, onTaskClick, isFirstColumn = false }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      columnId: column.id,
      accepts: ['task'],
    },
  });

  const collapsedWidth = '80px';
  const expandedWidth = '320px';

  const [internalExpanded, setInternalExpanded] = useState(isFirstColumn);

  const toggleExpanded = () => {
    if (!isFirstColumn) {
      setInternalExpanded(prev => !prev);
    }
  };

  const tasks = Array.isArray(column.tasks) ? column.tasks : [];
  const taskIds = tasks.map(task => task.id);

  return (
    <Box
      position="relative"
      height="500px"
      width={internalExpanded ? expandedWidth : collapsedWidth}
      minWidth={internalExpanded ? expandedWidth : collapsedWidth}
      transition="width 0.3s ease"
      cursor={!isFirstColumn ? 'pointer' : 'default'}
      userSelect="none"
    >
      {!internalExpanded && (
        <Box
          ref={setNodeRef}
          position="absolute"
          left={0}
          top={0}
          width={collapsedWidth}
          height="100%"
          bg={isOver ? '#E3F2FD' : '#F4F5F7'}
          border={isOver ? '2px solid #0065FF' : '1px solid #DFE1E6'}
          borderRadius="6px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 1px 3px rgba(9, 30, 66, 0.12)"
          transition="all 0.2s ease"
          _hover={{
            bg: '#E4E6EA',
            borderColor: '#C1C7D0',
          }}
          cursor="pointer"
          onClick={toggleExpanded}
        >
          <Text
            fontSize="11px"
            fontWeight="700"
            color="#6B778C"
            transform="rotate(-90deg)"
            whiteSpace="nowrap"
            textTransform="uppercase"
            letterSpacing="0.3px"
            userSelect="none"
            mr="18px"
          >
            {column.title}
          </Text>
          <Box
            position="absolute"
            top="8px"
            right="50%"
            transform="translateX(50%)"
            bg="#0065FF"
            color="white"
            fontSize="9px"
            fontWeight="600"
            width="18px"
            height="18px"
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            userSelect="none"
          >
            {tasks.length}
          </Box>
          <Box
            position="absolute"
            top="10px"
            right="calc(50% - 24px)"
            userSelect="none"
            pointerEvents="none"
          >
            <ChevronRightIcon boxSize={4} color="#6B778C" />
          </Box>
        </Box>
      )}

      {internalExpanded && (
        <Box
          ref={setNodeRef}
          display="flex"
          flexDirection="column"
          width="100%"
          height="100%"
          bg="white"
          border="1px solid #DFE1E6"
          borderRadius="6px"
          py={4}
          px={1}
          boxShadow="0 1px 3px rgba(9, 30, 66, 0.12)"
          overflow="hidden"
          willChange="width"
        >
          <Flex
            align="center"
            mb={4}
            gap={2}
            cursor={!isFirstColumn ? 'pointer' : 'default'}
            onClick={() => {
              if (!isFirstColumn) setInternalExpanded(false);
            }}
            userSelect="none"
            px={1}
            py={1}
            borderRadius="4px"
            _hover={!isFirstColumn ? { bg: '#F4F5F7' } : {}}
            transition="background 0.2s ease"
          >
            <Text
              fontSize="12px"
              fontWeight="700"
              color="#6B778C"
              textTransform="uppercase"
              letterSpacing="0.5px"
              flex="1"
            >
              {column.title}
            </Text>

            <Box
              bg="#DFE1E6"
              color="#6B778C"
              fontSize="11px"
              fontWeight="600"
              px={2}
              py={1}
              borderRadius="12px"
              minW="20px"
              textAlign="center"
            >
              {tasks.length}
            </Box>

            {!isFirstColumn && (
              <Box userSelect="none" pointerEvents="none">
                <ChevronLeftIcon boxSize={5} color="#6B778C" />
              </Box>
            )}
          </Flex>

          <Box
            flex="1"
            overflowY="auto"
            borderRadius="4px"
            px={1}
            py={1}
            bg={isOver ? '#E3F2FD' : 'transparent'}
            border={isOver ? '2px dashed #0065FF' : '1px solid transparent'}
            transition="all 0.2s ease"
            minHeight="100px"
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#C1C7D0',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#A5ADBA',
              },
            }}
          >
            <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
              <VStack spacing={2} align="stretch">
                {tasks.map(task => (
                  <SortableTask
                    key={task.id}
                    task={task}
                    columnId={column.id}
                    onTaskClick={taskData => {
                      // console.log(`DroppableColumn: Task clicked`, taskData);
                      if (onTaskClick && typeof onTaskClick === 'function') {
                        onTaskClick(taskData);
                      } else {
                        console.error('onTaskClick is not a function');
                      }
                    }}
                  />
                ))}
                {tasks.length === 0 && (
                  <Box
                    height="50px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="#6B778C"
                    fontSize="12px"
                    fontStyle="italic"
                    border="1px dashed"
                    borderColor={isOver ? '#0065FF' : '#DFE1E6'}
                    borderRadius="4px"
                    bg={isOver ? '#E3F2FD' : 'transparent'}
                  >
                    Drop cards here
                  </Box>
                )}
              </VStack>
            </SortableContext>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default DroppableColumn;